import type { MobilePhone, Rial } from "@kitchen/schemas";
import { Button, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrTempWorkersApi } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";

export function TempWorkersTab() {
  const [showAddWorkerModal, setShowAddWorkerModal] = useState(false);
  const [showLogRecordModal, setShowLogRecordModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  // New worker form
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [rateType, setRateType] = useState<"daily" | "hourly">("daily");
  const [rateAmount, setRateAmount] = useState<number>(10_000_000);
  const [notes, setNotes] = useState("");

  // Work record form
  const [recordWorkerId, setRecordWorkerId] = useState("");
  const [recordDate, setRecordDate] = useState(new Date().toISOString().slice(0, 10));
  const [recordDaysWorked, setRecordDaysWorked] = useState<number>(1);
  const [recordHoursWorked, setRecordHoursWorked] = useState<number>(8);
  const [recordNotes, setRecordNotes] = useState("");

  // Pay form
  const [payMethod, setPayMethod] = useState<"cash" | "card" | "bank_transfer" | "other">("cash");
  const [payRef, setPayRef] = useState("");

  const workersQuery = useQuery({
    queryKey: ["hr", "temp-workers"],
    queryFn: () => hrTempWorkersApi.listTempWorkers(),
  });

  const recordsQuery = useQuery({
    queryKey: ["hr", "temp-workers", "records"],
    queryFn: () => hrTempWorkersApi.listTempWorkerRecords(),
  });

  const addWorkerAction = useAction(async () => {
    await hrTempWorkersApi.createTempWorker({
      name: name.trim(),
      mobile: mobile.trim() as MobilePhone,
      nationalId: nationalId.trim(),
      rateType,
      rateAmount: rateAmount as Rial,
      notes: notes.trim(),
    });

    setShowAddWorkerModal(false);
    setName("");
    setMobile("");
    setNationalId("");
    setNotes("");
  }, [["hr", "temp-workers"]]);

  const logRecordAction = useAction(async () => {
    const selectedWorker = workersQuery.data?.find((w) => w.id === recordWorkerId);
    const isDaily = selectedWorker?.rateType === "daily";

    await hrTempWorkersApi.logTempWorkerRecord({
      workerId: recordWorkerId,
      date: recordDate,
      daysWorked: isDaily ? recordDaysWorked : 0,
      hoursWorked: isDaily ? 0 : recordHoursWorked,
      eventId: null,
      notes: recordNotes.trim(),
    });

    setShowLogRecordModal(false);
    setRecordWorkerId("");
    setRecordNotes("");
  }, [["hr", "temp-workers", "records"]]);

  const payRecordAction = useAction(async () => {
    if (!selectedRecordId) return;

    await hrTempWorkersApi.markTempWorkerPaid(selectedRecordId, {
      paymentMethod: payMethod,
      paymentRef: payRef.trim(),
    });

    setShowPayModal(false);
    setSelectedRecordId(null);
    setPayRef("");
  }, [["hr", "temp-workers", "records"]]);

  const workers = workersQuery.data ?? [];
  const records = recordsQuery.data ?? [];

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {/* Workers Panel */}
      <Panel
        title="فهرست نیروهای موقت و روزمزد"
        actions={
          <div className="row" style={{ gap: "var(--s2)" }}>
            <Button
              variant="default"
              onClick={() => {
                setShowLogRecordModal(true);
              }}
              id="btn-open-log-record"
            >
              ثبت کارکرد روزانه
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowAddWorkerModal(true);
              }}
              id="btn-add-temp-worker"
            >
              ثبت نیروی موقت جدید
            </Button>
          </div>
        }
      >
        <p className="muted" style={{ margin: "0 0 var(--s3) 0" }}>
          ثبت سریع مشخصات کارگران موقت و روزمزد همراه با نوع دستمزد (روزمزد یا ساعتی)
        </p>

        {workersQuery.isLoading && <p className="muted">در حال دریافت فهرست نیروها...</p>}

        {!workersQuery.isLoading && workers.length === 0 && (
          <p className="muted" style={{ textAlign: "center", padding: "var(--s4)" }}>
            هیچ نیروی موقتی ثبت نشده است.
          </p>
        )}

        {workers.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>نام و نام خانوادگی</th>
                  <th>شماره تماس</th>
                  <th>کد ملی</th>
                  <th>نوع نرخ</th>
                  <th>مبلغ نرخ</th>
                  <th>یادداشت</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {workers.map((w) => (
                  <tr key={w.id}>
                    <td>
                      <strong>{w.name}</strong>
                    </td>
                    <td dir="ltr" style={{ textAlign: "right" }}>
                      {w.mobile}
                    </td>
                    <td>{w.nationalId || "—"}</td>
                    <td>
                      <span className={`chip ${w.rateType === "daily" ? "primary" : "secondary"}`}>
                        {w.rateType === "daily" ? "روزمزد" : "ساعتی"}
                      </span>
                    </td>
                    <td>
                      {w.rateAmount != null ? `${Number(w.rateAmount).toLocaleString("fa-IR")} ریال` : "—"}
                    </td>
                    <td className="muted">{w.notes || "—"}</td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setRecordWorkerId(w.id);
                          setShowLogRecordModal(true);
                        }}
                      >
                        ثبت کارکرد
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Work Records Panel */}
      <Panel title="کارکرد روزانه و وضعیت تسویه دستمزد">
        <p className="muted" style={{ margin: "0 0 var(--s3) 0" }}>
          ثبت ساعات یا روزهای کارکرد، محاسبه خودکار دستمزد و ثبت تسویه حساب با ارجاع پرداخت
        </p>

        {recordsQuery.isLoading && <p className="muted">در حال بارگذاری سوابق کارکرد...</p>}

        {!recordsQuery.isLoading && records.length === 0 && (
          <p className="muted" style={{ textAlign: "center", padding: "var(--s4)" }}>
            هنوز سابقه‌ای برای کارکرد روزانه ثبت نشده است.
          </p>
        )}

        {records.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>کارگر</th>
                  <th>تاریخ</th>
                  <th>میزان کار</th>
                  <th>دستمزد محاسبه‌شده</th>
                  <th>وضعیت پرداخت</th>
                  <th>روش پرداخت</th>
                  <th>شناسه ارجاع</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <strong>{r.workerName}</strong>
                      <div className="muted" style={{ fontSize: "0.8rem" }}>
                        {r.workerMobile}
                      </div>
                    </td>
                    <td>{r.date}</td>
                    <td>
                      {r.rateType === "daily"
                        ? `${String(r.daysWorked)} روز`
                        : `${String(r.hoursWorked)} ساعت`}
                    </td>
                    <td>
                      {r.calculatedWage != null
                        ? `${Number(r.calculatedWage).toLocaleString("fa-IR")} ریال`
                        : "—"}
                    </td>
                    <td>
                      {r.isPaid ? (
                        <span className="chip green">تسویه شده</span>
                      ) : (
                        <span className="chip amber">در انتظار تسویه</span>
                      )}
                    </td>
                    <td>
                      {r.paymentMethod
                        ? r.paymentMethod === "cash"
                          ? "نقدی"
                          : r.paymentMethod === "card"
                            ? "کارت‌به‌کارت"
                            : r.paymentMethod === "bank_transfer"
                              ? "حواله پایا"
                              : "سایر"
                        : "—"}
                    </td>
                    <td>{r.paymentRef || "—"}</td>
                    <td>
                      {!r.isPaid && (
                        <Button
                          variant="primary"
                          id={`btn-pay-${r.id}`}
                          className="btn-pay-record"
                          onClick={() => {
                            setSelectedRecordId(r.id);
                            setShowPayModal(true);
                          }}
                        >
                          تسویه
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Modal: Add Temp Worker */}
      {showAddWorkerModal && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setShowAddWorkerModal(false);
          }}
        >
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ maxWidth: 500 }}
          >
            <h3>ثبت نیروی موقت جدید</h3>

            <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
              <Field
                id="temp-worker-name"
                label="نام و نام خانوادگی"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="مثال: قاسم رضایی"
              />

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <Field
                  id="temp-worker-mobile"
                  label="شماره موبایل"
                  type="tel"
                  value={mobile}
                  onChange={(e) => {
                    setMobile(e.target.value);
                  }}
                  placeholder="09121234567"
                  dir="ltr"
                  style={{ flex: 1 }}
                />

                <Field
                  id="temp-worker-national-id"
                  label="کد ملی (اختیاری)"
                  value={nationalId}
                  onChange={(e) => {
                    setNationalId(e.target.value);
                  }}
                  placeholder="10 رقمی"
                  dir="ltr"
                  style={{ flex: 1 }}
                />
              </div>

              <div className="row wrap" style={{ gap: "var(--s3)" }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="temp-worker-rate-type">نوع دستمزد</label>
                  <select
                    id="temp-worker-rate-type"
                    className="input"
                    value={rateType}
                    onChange={(e) => {
                      setRateType(e.target.value as "daily" | "hourly");
                    }}
                  >
                    <option value="daily">روزمزد (به ازای هر روز)</option>
                    <option value="hourly">ساعتی (به ازای هر ساعت)</option>
                  </select>
                </div>

                <Field
                  id="temp-worker-rate-amount"
                  label="مبلغ نرخ (ریال)"
                  type="number"
                  value={rateAmount}
                  onChange={(e) => {
                    setRateAmount(Number(e.target.value));
                  }}
                  step={100_000}
                  style={{ flex: 1 }}
                />
              </div>

              <Field
                id="temp-worker-notes"
                label="یادداشت و مهارت‌ها"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                placeholder="مثال: کمک پخت برنج، شستشوی دیگ"
              />

              <ErrorLine error={addWorkerAction.error} />

              <div
                className="row"
                style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowAddWorkerModal(false);
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    addWorkerAction.run(undefined);
                  }}
                  disabled={addWorkerAction.busy || !name.trim() || !mobile.trim()}
                  id="btn-submit-temp-worker"
                >
                  {addWorkerAction.busy ? "در حال ثبت..." : "ثبت نیرو"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Log Work Record */}
      {showLogRecordModal && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setShowLogRecordModal(false);
          }}
        >
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ maxWidth: 500 }}
          >
            <h3>ثبت کارکرد روزانه</h3>

            <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="record-worker-select">انتخاب نیروی موقت</label>
                <select
                  id="record-worker-select"
                  className="input"
                  value={recordWorkerId}
                  onChange={(e) => {
                    setRecordWorkerId(e.target.value);
                  }}
                >
                  <option value="">-- انتخاب نیرو --</option>
                  {workers.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.rateType === "daily" ? "روزمزد" : "ساعتی"})
                    </option>
                  ))}
                </select>
              </div>

              <Field
                id="record-date-input"
                label="تاریخ کارکرد"
                type="date"
                value={recordDate}
                onChange={(e) => {
                  setRecordDate(e.target.value);
                }}
              />

              {(() => {
                const selected = workers.find((w) => w.id === recordWorkerId);
                const isHourly = selected?.rateType === "hourly";

                return isHourly ? (
                  <Field
                    id="record-hours-input"
                    label="ساعات کارکرد"
                    type="number"
                    step={0.5}
                    min={0}
                    max={24}
                    value={recordHoursWorked}
                    onChange={(e) => {
                      setRecordHoursWorked(Number(e.target.value));
                    }}
                  />
                ) : (
                  <Field
                    id="record-days-input"
                    label="تعداد روز کارکرد"
                    type="number"
                    step={0.5}
                    min={0}
                    max={1}
                    value={recordDaysWorked}
                    onChange={(e) => {
                      setRecordDaysWorked(Number(e.target.value));
                    }}
                  />
                );
              })()}

              <Field
                id="record-notes-input"
                label="یادداشت کارکرد"
                value={recordNotes}
                onChange={(e) => {
                  setRecordNotes(e.target.value);
                }}
                placeholder="مثال: شیفت شام مراسم عروسی"
              />

              <ErrorLine error={logRecordAction.error} />

              <div
                className="row"
                style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowLogRecordModal(false);
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    logRecordAction.run(undefined);
                  }}
                  disabled={logRecordAction.busy || !recordWorkerId}
                  id="btn-submit-work-record"
                >
                  {logRecordAction.busy ? "در حال ثبت..." : "ثبت کارکرد"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Mark Paid */}
      {showPayModal && (
        <div
          className="modal-backdrop"
          onClick={() => {
            setShowPayModal(false);
          }}
        >
          <div
            className="modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{ maxWidth: 460 }}
          >
            <h3>تسویه حساب و پرداخت دستمزد</h3>

            <div className="stack" style={{ gap: "var(--s3)", marginTop: "var(--s3)" }}>
              <div className="field">
                <label htmlFor="pay-method-select">روش پرداخت</label>
                <select
                  id="pay-method-select"
                  className="input"
                  value={payMethod}
                  onChange={(e) => {
                    setPayMethod(e.target.value as "cash" | "card" | "bank_transfer" | "other");
                  }}
                >
                  <option value="cash">نقدی</option>
                  <option value="card">کارت‌به‌کارت</option>
                  <option value="bank_transfer">حواله بانکی پایا / ساتنا</option>
                  <option value="other">سایر</option>
                </select>
              </div>

              <Field
                id="pay-ref-input"
                label="شماره پیگیری یا ارجاع پرداخت"
                value={payRef}
                onChange={(e) => {
                  setPayRef(e.target.value);
                }}
                placeholder="مثال: TRX-192837 یا رسید دستی"
              />

              <ErrorLine error={payRecordAction.error} />

              <div
                className="row"
                style={{ gap: "var(--s2)", justifyContent: "flex-end", marginTop: "var(--s3)" }}
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowPayModal(false);
                  }}
                >
                  انصراف
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    payRecordAction.run(undefined);
                  }}
                  disabled={payRecordAction.busy}
                  id="btn-confirm-pay"
                >
                  {payRecordAction.busy ? "در حال ثبت تسویه..." : "تأیید تسویه"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
